<template>
  <div data-test-id="user-card-row" class="user-row" @click="openProfileDetails">
    <div>
      <UserInfo :user="user" :sub-info="shownEmailRef" :is-sub-info-copyable="true" />
    </div>
    <div class="flex flex-wrap gap-2">
      <Badge v-if="isSuperAdminRef" data-test-id="program-user-super-admin-badge" variant="warning">
        {{ t('admin.users.user.superAdmin') }}
      </Badge>
      <Badge
        v-if="user.roles.length > 0"
        data-test-id="user-card-role"
        v-for="role in user.roles"
        :key="role"
        variant="black"
      >
        {{ t(userRolesNamesMap[role]) }}
      </Badge>
    </div>
    <div class="user-row__group">
      <Badge v-if="user.roles.length > 0" :variant="user.hasGroup ? 'info' : 'danger'">
        {{ user.hasGroup ? t('admin.users.user.inAGroup') : t('admin.users.user.withoutGroup') }}
      </Badge>
    </div>
    <div
      class="user-row__actions"
      data-action="user-row"
      :data-attached="JSON.stringify({ user: user, program: programRef })"
    >
      <MoreOptionsIcon
        v-if="programRolesRef?.includes(RoleEnumTypeV2.ADMIN)"
        data-test-id="user-row-actions"
        color="black"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { RoleEnumTypeV2 } from '@/features/auth/types'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programStore from '@/features/programs/stores/programs/programs.store'
import MoreOptionsIcon from '@/features/ui/components/icons/more-options-icon.vue'
import type { UserProfileModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import UserInfo from '@/features/users/components/user-info/user-info.vue'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import type { V2UserAdminDataType } from '@/features/users/types'
import { userRolesNamesMap } from '@/features/users/utils'
import { useI18n } from '@/lib/i18n'
import Badge from '@/components/Badge.vue'

const { t } = useI18n()

const shownEmailRef = computed(() => props.user.email)
const isSuperAdminRef = computed(
  () => props.user.roles.includes(RoleEnumTypeV2.ADMIN) && props.user.roles.includes(RoleEnumTypeV2.SUPPORT),
)
const selectedProgramIdRef = selectGetSelectedProgramId(programStore)
const { programRef } = useProgram(selectedProgramIdRef)
const { programRolesRef } = useProgramRoles()

const props = defineProps<{
  user: V2UserAdminDataType
}>()

function openProfileDetails(e: Event) {
  // guard to not open the modal when clicking on the more options icon
  // guard to not open the modal when clicking on the sub info and this one is copyable
  if (
    (e.target as HTMLElement).closest('[data-action="user-row"]') ||
    (e.target as HTMLElement).closest('[data-sub-info-copyable="true"]')
  ) {
    return
  }

  modalsStore.toggleModal('user-profile', true)
  modalsStore.updateModalAttachedData<UserProfileModalType>('user-profile', { userId: props.user.id! })
}
</script>

<style lang="scss">
@import './user-row.scss';
</style>
